import { type ClockState, type ConnectorId, clockStateSchema, type Me } from '@hugo/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FastForwardIcon,
  LogOutIcon,
  RadioIcon,
  RotateCcwIcon,
  SearchIcon,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import { z } from 'zod'
import { AiCard } from '@/components/ai-card'
import { ConnectionsCard } from '@/components/connections-card'
import { McpCard } from '@/components/mcp-card'
import { RulesCard } from '@/components/rules-card'
import { TagsCard } from '@/components/tags-card'
import { TimeOffCard } from '@/components/time-off-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { toast } from '@/components/ui/toast'
import { View } from '@/components/view'
import { VoiceCard } from '@/components/voice-card'
import { WorkingTimeCard } from '@/components/working-time-card'
import { apiFetch } from '@/lib/api'
import { formatDateTime } from '@/lib/format'
import {
  useAccessTokens,
  useAiStatus,
  useConnectors,
  useSettings,
  useTags,
  useTimeOff,
} from '@/lib/items'
import { timeOffState, timeOffText, userToday } from '@/lib/plan'
import { platform } from '@/lib/platform'
import { meQueryKey, useSession, useUserNow } from '@/lib/session'
import {
  aiSummary,
  appsSummary,
  matchesSearch,
  rulesSummary,
  SETTINGS_GROUPS,
  SETTINGS_SECTIONS,
  type SettingsSection,
  tagsSummary,
  tokensSummary,
  workingTimeSummary,
} from '@/lib/settings-sections'
import { type Theme, useTheme } from '@/lib/theme'

const DAY_MS = 86_400_000

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right">{children}</span>
    </div>
  )
}

function TestTools() {
  const { me } = useSession()
  const now = useUserNow(1000)
  const queryClient = useQueryClient()
  // Apply the clock from the response right away; live updates only matter for other tabs.
  const applyClock = (clock: ClockState) =>
    queryClient.setQueryData<Me | null>(meQueryKey, (prev) =>
      prev ? { ...prev, now: clock.now, clockOffsetMs: clock.offsetMs } : prev,
    )
  const advance = useMutation({
    mutationFn: (ms: number) =>
      apiFetch('/api/e2e/clock/advance', clockStateSchema, { method: 'POST', json: { ms } }),
    onSuccess: applyClock,
  })
  const reset = useMutation({
    mutationFn: () => apiFetch('/api/e2e/clock/reset', clockStateSchema, { method: 'POST' }),
    onSuccess: applyClock,
  })
  const ping = useMutation({
    mutationFn: () =>
      apiFetch('/api/e2e/ping', z.object({ seq: z.number() }), {
        method: 'POST',
        json: { message: `Ping at ${new Date().toLocaleTimeString()}` },
      }),
    onSuccess: () => toast.add({ title: 'Test ping sent', type: 'success' }),
  })
  if (!me?.testMode) return null
  return (
    <Card data-testid="test-tools">
      <CardHeader>
        <CardTitle>Test tools</CardTitle>
        <CardDescription>Only available on test servers.</CardDescription>
        <CardAction>
          <Badge variant="outline">Test mode</Badge>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Row label="Clock">
          <span data-testid="clock-now">{formatDateTime(now)}</span>
          {me.clockOffsetMs !== 0 ? (
            <Badge variant="secondary" className="ml-2" data-testid="clock-offset">
              {me.clockOffsetMs > 0 ? '+' : ''}
              {Math.round(me.clockOffsetMs / DAY_MS)}d
            </Badge>
          ) : null}
        </Row>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => advance.mutate(DAY_MS)}
            disabled={advance.isPending}
          >
            <FastForwardIcon aria-hidden="true" />
            Advance 1 day
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => reset.mutate()}
            disabled={reset.isPending}
          >
            <RotateCcwIcon aria-hidden="true" />
            Reset clock
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => ping.mutate()}
            disabled={ping.isPending}
          >
            <RadioIcon aria-hidden="true" />
            Send test ping
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const THEMES: { value: Theme; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
]

/** A section's line on the overview: its name, what it is set to now, and a way in. */
function SectionRow({ section, summary }: { section: SettingsSection; summary: string | null }) {
  return (
    <Link
      to="/settings/$section"
      params={{ section: section.id }}
      data-testid="settings-row"
      data-section={section.id}
      className="flex min-h-14 items-center gap-3 px-4 py-3 outline-none hover:bg-muted/50 focus-visible:bg-muted/60 focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-medium">{section.title}</span>
        {summary ? (
          <span className="truncate text-sm text-muted-foreground" data-testid="settings-summary">
            {summary}
          </span>
        ) : null}
      </span>
      <ChevronRightIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
    </Link>
  )
}

/**
 * Settings: one page, grouped (how hugo plans, what is connected, you), each section with what it
 * is set to now, so most questions are answered without opening anything. Each section opens its
 * own page (`/settings/<section>`); search filters by name, content and summary.
 */
export function SettingsPage() {
  const { me } = useSession()
  const { theme, setTheme } = useTheme()
  const now = useUserNow(60_000)
  const settings = useSettings().data
  const periods = useTimeOff().data ?? []
  const tags = useTags().data ?? []
  const connectors = useConnectors().data?.connectors ?? []
  const ai = useAiStatus().data
  const tokens = useAccessTokens().data
  const [query, setQuery] = useState('')
  if (!me) return null

  const { today, year } = userToday(now, settings?.timezone ?? 'Europe/Zurich')
  const nextOff = periods.find((p) => timeOffState(p, today) !== 'ended')
  const summaries: Record<string, string | null> = {
    'working-time': settings ? workingTimeSummary(settings) : null,
    'time-off': nextOff ? timeOffText(nextOff, year) : 'Nothing planned',
    rules: settings ? rulesSummary(settings.rules) : null,
    tags: settings
      ? tagsSummary(
          tags.map((t) => t.name),
          settings.personalTags,
        )
      : null,
    apps: appsSummary(
      connectors.flatMap((c) =>
        c.connection ? [{ title: c.title, status: c.connection.status }] : [],
      ),
    ),
    ai: ai ? aiSummary(ai) : null,
    'claude-code': tokens ? tokensSummary(tokens, now) : null,
    voice: settings ? (settings.speechLanguage === 'de' ? 'German' : 'English') : null,
    account: `${me.user.name} · ${me.user.email}`,
    about: me.version,
  }
  const visible = (section: SettingsSection) =>
    matchesSearch(section, query, summaries[section.id] ?? '')
  const showTheme = matchesSearch(
    { id: 'appearance', title: 'Appearance', group: 'you', keywords: ['theme', 'dark', 'light'] },
    query,
    '',
  )
  const about = SETTINGS_SECTIONS.find((s) => s.id === 'about')
  const nothing = !showTheme && !SETTINGS_SECTIONS.some((s) => s.group !== 'footer' && visible(s))

  return (
    <View
      name="settings"
      title="Settings"
      actions={
        <div className="relative w-full sm:w-64">
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label="Search settings"
            placeholder="Search settings"
            className="h-9 pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      }
    >
      {SETTINGS_GROUPS.map((group) => {
        const sections = SETTINGS_SECTIONS.filter((s) => s.group === group.id && visible(s))
        const withTheme = group.id === 'you' && showTheme
        if (sections.length === 0 && !withTheme) return null
        return (
          <section
            key={group.id}
            aria-labelledby={`settings-${group.id}`}
            className="flex flex-col gap-2"
          >
            <h2
              id={`settings-${group.id}`}
              className="px-1 text-sm font-medium text-muted-foreground"
            >
              {group.title}
            </h2>
            <div className="divide-y overflow-hidden rounded-xl border bg-card">
              {withTheme ? (
                <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <span className="font-medium" id="appearance-label">
                    Appearance
                  </span>
                  <fieldset aria-labelledby="appearance-label" className="flex gap-1">
                    {THEMES.map((t) => (
                      <Button
                        key={t.value}
                        variant={theme === t.value ? 'default' : 'outline'}
                        size="sm"
                        aria-pressed={theme === t.value}
                        onClick={() => setTheme(t.value)}
                      >
                        {t.label}
                      </Button>
                    ))}
                  </fieldset>
                </div>
              ) : null}
              {sections.map((section) => (
                <SectionRow
                  key={section.id}
                  section={section}
                  summary={summaries[section.id] ?? null}
                />
              ))}
            </div>
          </section>
        )
      })}
      {nothing ? (
        <p
          className="py-8 text-center text-sm text-muted-foreground"
          data-testid="settings-no-match"
        >
          No settings match “{query.trim()}”.
        </p>
      ) : null}
      {about ? (
        <p className="px-1 text-xs text-muted-foreground">
          hugo {me.version} ·{' '}
          <Link
            to="/settings/$section"
            params={{ section: about.id }}
            className="underline-offset-4 hover:underline"
          >
            About
          </Link>
        </p>
      ) : null}
    </View>
  )
}

/**
 * One section of Settings (`/settings/<section>`), with a way back. `connect` / `linkError`: Google
 * sent the user back to Apps after asking for access.
 */
export function SettingsSectionPage({
  section,
  connect,
  linkError,
  oauthResult,
}: {
  section: string
  connect?: ConnectorId | undefined
  linkError?: string | undefined
  oauthResult?: { connector: ConnectorId; error: string | null } | undefined
}) {
  const { me, signOut, live } = useSession()
  const navigate = useNavigate()
  const clearConnectParams = useCallback(() => {
    void navigate({
      to: '/settings/$section',
      params: { section: 'apps' },
      search: {},
      replace: true,
    })
  }, [navigate])
  if (!me) return null
  const meta = SETTINGS_SECTIONS.find((s) => s.id === section)
  if (!meta) {
    return (
      <View name="settings" title="Settings">
        <p className="text-sm text-muted-foreground">
          There is no such setting.{' '}
          <Link to="/settings" className="underline underline-offset-4">
            Back to Settings
          </Link>
        </p>
      </View>
    )
  }
  return (
    <View name="settings" title={meta.title} className="gap-4">
      <Link
        to="/settings"
        data-testid="settings-back"
        className="-mt-2 inline-flex w-fit items-center gap-1 rounded-md text-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <ChevronLeftIcon aria-hidden="true" className="size-4" />
        Settings
      </Link>
      {section === 'working-time' ? <WorkingTimeCard /> : null}
      {section === 'time-off' ? <TimeOffCard /> : null}
      {section === 'rules' ? <RulesCard /> : null}
      {section === 'tags' ? <TagsCard /> : null}
      {section === 'apps' ? (
        <ConnectionsCard
          connectRequest={connect}
          linkError={linkError}
          oauthResult={oauthResult}
          onRequestHandled={clearConnectParams}
        />
      ) : null}
      {section === 'ai' ? <AiCard /> : null}
      {section === 'claude-code' ? <McpCard /> : null}
      {section === 'voice' ? <VoiceCard /> : null}
      {section === 'account' ? (
        <Card>
          <CardContent>
            <Row label="Name">{me.user.name}</Row>
            <Separator />
            <Row label="Email">{me.user.email}</Row>
            <Separator />
            <div className="pt-3">
              <Button variant="outline" onClick={() => void signOut()}>
                <LogOutIcon aria-hidden="true" />
                Sign out
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
      {section === 'about' ? (
        <>
          <Card>
            <CardContent>
              <Row label="Version">
                <span data-testid="app-version">{me.version}</span>
              </Row>
              <Separator />
              <Row label="Platform">{platform}</Row>
              <Separator />
              <Row label="Live updates">{live}</Row>
            </CardContent>
          </Card>
          <TestTools />
        </>
      ) : null}
    </View>
  )
}
